import { type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Equal-width segment toggle. Selected uses odds-selected purple; idle uses gold text
 * (Figma Login method toggle).
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex h-[34px] w-full items-center gap-2', className)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <SegmentButton
            key={option.value}
            selected={selected}
            disabled={option.disabled}
            onClick={() => {
              if (!option.disabled) onChange(option.value);
            }}
          >
            {option.label}
          </SegmentButton>
        );
      })}
    </div>
  );
}

interface SegmentButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected: boolean;
}

function SegmentButton({ selected, className, children, ...props }: SegmentButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={cn(
        'flex flex-1 items-center justify-center rounded-md px-4 py-2 text-[13px] leading-[18px] font-bold',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        selected
          ? 'bg-odds-selected text-odds-selected-text'
          : 'bg-transparent text-[#ffe080] hover:bg-bg-elevated',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
